import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { App, Button, Col, Form, Grid, InputNumber, Modal, Row, Table, Typography } from "antd";
import { ArrowLeftOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import InputField from "../../components/Input/Input";
import { fetchManufactures, createManufactures } from "../Product_manage/itemManageThunks";
import { getAccessType } from "../../lib/functions";
import { useIsAdminUser } from "../../lib/accessList";

const { Title } = Typography;

const Manufacturers = () => {
  const { modal } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);
  const canEdit = isAdminUser || authInfo?.can_edit === 1;

  const [accessType, setAccessType] = useState("");
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadManufacturers = async () => {
    setLoading(true);
    try {
      const list = await dispatch(fetchManufactures()).unwrap();
      setManufacturers(Array.isArray(list) ? list : []);
    } catch (err) {
      modal.error({ title: err?.message || "Failed to load manufacturers" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAccessType(getAccessType("item_entry"));
    loadManufacturers();
  }, []);

  const filteredManufacturers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return manufacturers;
    return manufacturers.filter((m) => m.name?.toLowerCase().includes(term));
  }, [manufacturers, search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      company_prefix_length: record.company_prefix_length ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const res = await dispatch(
        createManufactures({
          action: editing ? "update" : "create",
          ...(editing && { id: editing.id }),
          name: values.name.trim(),
          company_prefix_length: values.company_prefix_length || null,
        })
      ).unwrap();

      if (res?.error) {
        modal.error({ title: res.msg });
        return;
      }
      modal.success({ title: res?.msg });
      setModalOpen(false);
      loadManufacturers();
    } catch (err) {
      modal.error({ title: err?.message || "Failed to save manufacturer" });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "Manufacturer Name", dataIndex: "name" },
    {
      title: "Company Prefix Length",
      dataIndex: "company_prefix_length",
      render: (value) => value ?? "-",
    },
    ...(accessType === "WRITE"
      ? [
          {
            title: "Actions",
            render: (_, record) => (
              <Button
                type="link"
                disabled={!canEdit}
                onClick={() => openEdit(record)}
                icon={<EditOutlined />}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: !screens.md ? "18px" : "22px" }} />}
          onClick={() => navigate("/inventory/dashboard")}
          style={{ marginRight: 12, padding: 0, height: "auto", lineHeight: "1", color: "#000" }}
        />
        <Title
          level={!screens.md ? 4 : 2}
          style={{ margin: 0, whiteSpace: "normal", wordBreak: "break-word" }}
        >
          Manufacturers
        </Title>
      </div>

      <Row justify="space-between" gutter={[12, 12]} style={{ marginBottom: "24px" }}>
        <Col xs={24} md={8}>
          <InputField
            placeholder="Search manufacturer"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, current: 1 }));
            }}
            allowClear
          />
        </Col>
        <Col>
          {accessType === "WRITE" && (
            <Button type="primary" className="traic-button" icon={<PlusOutlined />} onClick={openCreate}>
              Add New
            </Button>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredManufacturers}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={(p) => setPagination({ current: p.current, pageSize: p.pageSize })}
        scroll={{ x: "max-content" }}
      />

      <Modal
        open={modalOpen}
        title={editing ? "Edit Manufacturer" : "Add Manufacturer"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText="Save"
        confirmLoading={saving}
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Manufacturer Name"
            name="name"
            rules={[{ required: true, whitespace: true, message: "Manufacturer name is required" }]}
          >
            <InputField placeholder="Enter Manufacturer Name" />
          </Form.Item>
          <Form.Item label="Company Prefix Length" name="company_prefix_length">
            <InputNumber
              min={6}
              max={12}
              precision={0}
              placeholder="6-12 (optional)"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Manufacturers;
